import type { ApiKeyEntity } from "@/modules/dashboard/settings/services/apiKeysService";

/**
 * The three states a key can actually be in at the gate, which is NOT the same as its `is_active` column.
 *
 * `ApiKeyAuthGuard` rejects on two independent grounds: the flag, and an `expires_at` in the past. A key can
 * therefore sit at `is_active: true` and refuse every request — and the section this view replaces would
 * render it as plain "active", which is the one answer that is certainly wrong. `expires_at` already travels
 * in the list payload, so telling them apart costs nothing.
 *
 * There is no fourth state for "expiring soon": the backend has no such notion, and inventing a threshold
 * here would put a deadline on screen that nothing enforces.
 */
export type ApiKeyStatus = "active" | "expired" | "inactive";

/**
 * Module-level, like the section's `formatLastUsed`, so the unavoidable `Date.now()` is not a clock read
 * inside a component or hook body — which the purity rule rejects, and rightly: a value that changes on every
 * re-render does not belong in one.
 *
 * The drift between two rows of the same render is microseconds, against an `expires_at` measured in days.
 */
export function resolveApiKeyStatus(key: ApiKeyEntity): ApiKeyStatus {
  // The flag wins: a key that is both disabled and expired is disabled, because that is the deliberate act.
  if (!key.is_active) return "inactive";
  if (key.expires_at && new Date(key.expires_at).getTime() <= Date.now()) return "expired";
  return "active";
}
